import { DailyPnlItem } from '@/apis/idea/idea-api.def.ts'
import { useAppSelector } from '@/redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '@/features/setting/setting.slice.ts'
import { EChartsOption, SeriesOption } from 'echarts'
import dayjs from 'dayjs'
import { currencyValueFormatter, formatCurrency } from '@/utils/formatters.ts'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { getEchartsTheme } from '@/utils/helpers.ts'
import { useMemo } from 'react'

interface PnlLineChartProps {
  bookActiveDate: string
  data: DailyPnlItem[]
  currency: string
}

export const IdeaPnlLineChart: React.FC<PnlLineChartProps> = ({
  bookActiveDate,
  data,
  currency,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const colorTheme = useAppSelector(selectTheme)
  const echartsTheme = getEchartsTheme(colorTheme)

  // Memoize the option so a re-render (e.g. antd breakpoint settling after mount)
  // does not create a new reference and force echarts to dispose/redraw the chart.
  const lineChartOptions = useMemo<EChartsOption>(() => {
    const sortedData = [...data].sort(
      (a, b) =>
        dayjs(a.positionDate).valueOf() - dayjs(b.positionDate).valueOf()
    )

    // Split data into before and after bookActiveDate
    const dateRange = sortedData.map((item) =>
      dayjs(item.positionDate).format('YYYY-MM-DD')
    )

    // Create datasets for before and after bookActiveDate
    const lineDataBefore = dateRange.map((date) => {
      const item = sortedData.find(
        (item) => dayjs(item.positionDate).format('YYYY-MM-DD') === date
      )
      return dayjs(item?.positionDate).isBefore(bookActiveDate)
        ? (item ?? null)
        : null
    })

    const lineDataAfter = dateRange.map((date) => {
      const item = sortedData.find(
        (item) => dayjs(item.positionDate).format('YYYY-MM-DD') === date
      )
      return dayjs(item?.positionDate).isSameOrAfter(bookActiveDate)
        ? (item ?? null)
        : null
    })

    const beforeCount = lineDataBefore.filter((item) => item !== null).length
    const afterCount = lineDataAfter.filter((item) => item !== null).length

    const lineSeries: SeriesOption[] = [
      {
        type: 'line',
        data: lineDataBefore.map((item) => ({
          value: item?.pnl,
          name: dayjs(item?.positionDate).format('YYYY-MM-DD'),
          raw: item,
        })),
        areaStyle: {
          color: themeStyle.colorDisableBg,
        },
        symbol: beforeCount === 1 ? 'emptyCircle' : 'none',
        itemStyle: {
          color: themeStyle.colorDisableBg,
        },
        lineStyle: {
          opacity: 0,
        },
      },
      {
        type: 'line',
        data: lineDataAfter.map((item) => ({
          value: item?.pnl ?? null,
          name: dayjs(item?.positionDate).format('YYYY-MM-DD'),
          raw: item,
        })),
        areaStyle: {},
        symbol: afterCount === 1 ? 'emptyCircle' : 'none',
        itemStyle: {
          color: '#b0de93',
        },
        lineStyle: {
          width: 2,
        },
      },
    ]

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          if (!Array.isArray(params)) return ''

          // Find the first series point that has a valid raw item
          const point = params.find((p) => {
            const item = (p.data as { raw?: DailyPnlItem })?.raw
            return item != null
          })

          if (!point) return ''

          const item = (point.data as { raw?: DailyPnlItem })?.raw
          if (!item) return ''

          return `
          <div style="
            border-radius: 8px;
            padding: 8px 12px;
          ">
            <div style="margin-bottom: 8px; font-size: 16px; font-weight: 500;"><b>${dayjs(
              item.positionDate
            ).format('YYYY/MM/DD')}</b></div>
            <table style="width:100%;border-collapse:collapse;">
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.shares')}</td>
                <td style="text-align:right; font-weight: 600">${item.shares.toLocaleString()}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.avg_cost')}</td>
                <td style="text-align:right; font-weight: 600;">${formatCurrency(item.avgCost, { currency: currency })}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.eod_price')}</td>
                <td style="text-align:right; font-weight: 600;">${formatCurrency(item.eodPrice, { currency: currency })}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t('idea_list.idea_modal.idea_pnl_line_chart.fx_rate')}</td>
                <td style="text-align:right; font-weight: 600;">${item.fxRate.toLocaleString(
                  undefined,
                  { maximumFractionDigits: 2 }
                )}</td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t(
                  'idea_list.idea_modal.idea_pnl_line_chart.realized_pnl'
                )}</td>
                <td style="text-align:right; font-weight: 600;">
                ${currencyValueFormatter(
                  { value: item.realizedPnl } as ValueFormatterParams,
                  {
                    decimal: 0,
                    currency: 'USD',
                  }
                )}
                </td>
              </tr>
              <tr>
                <td style="padding-right:16px;text-align:left;">${t(
                  'idea_list.idea_modal.idea_pnl_line_chart.unrealized_pnl'
                )}</td>
                <td style="text-align:right; font-weight: 600;">
                ${currencyValueFormatter(
                  { value: item.unrealizedPnl } as ValueFormatterParams,
                  {
                    decimal: 0,
                    currency: 'USD',
                  }
                )}
                </td>
              </tr>
            </table>
          </div>
        `
        },
      },
      xAxis: {
        type: 'category',
        data: dateRange,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => `$${value.toLocaleString()}`,
          margin: 16,
        },
      },
      series: lineSeries,
    }
  }, [data, bookActiveDate, currency, themeStyle, t])

  return (
    <div
      style={{
        padding: `${themeStyle.paddingXXS} ${themeStyle.paddingMD}`,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '8px',
        border: `1px solid ${themeStyle.sumCardOutline}`,
        background: themeStyle.sumCardBg,
        backdropFilter: 'blur(4px)',
      }}
    >
      <ReactECharts
        option={lineChartOptions}
        theme={echartsTheme.themeName}
        lazyUpdate
      />
    </div>
  )
}
